import { useState, useEffect, useRef, useCallback } from 'react';
import ImageLightbox from '../components/ImageLightbox';
import Avatar from '../components/Avatar';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import {
  ArrowLeft, Mail, Send, Search, Loader2, AlertCircle,
  MessageCircle, User as UserIcon, Users, Plus, Smile, Image as ImageIcon, Pencil, Check, CheckCheck,
  Mic, Contact as ContactIcon, X, Square, Info, Trash2, MoreVertical,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import api from '../services/api';
import { compressImageUnderCap, MAX_ATTACHMENT_BASE64 } from '../utils/imageCompress'; // UXFIX-IMGCOMPRESS-20260922
import { CondoOnboarding } from '../components/CondoLinking'; // E3/OBS-22: condo-less empty state
// I18N-2B (2026-09-23): user-visible copy extracted into the 'messages' namespace
// (es-MX base + en + thin es-VE/es-ES overrides). ICU plurals via i18next-icu.
// MessageTicks renders inside MensajesPage and always receives t as a prop, so
// no module-level translator is needed.

/**
 * Mensajes — DM page (F110, spec 2026-08-29).
 * Thread list (existing 1:1 DMs with last-message preview) + "Nuevo chat"
 * picker from the user's cross-condo DM directory (GET /me/dm-directory — union of
 * all condos the user shares membership with; independent of the active condo).
 * 1:1 chat view reuses the same composer as WallPage (emoji / image / voice note /
 * vCard contact) with attachments stored in the messages.attachments JSONB column,
 * same shape and 1.5 MB cap as the wall (F108). Text is optional when attachments
 * are present; sending goes through POST /communication/condo/:condoId/dm
 * (auth + requireCondoMembership, recipient must be a condo member).
 *
 * Polling (user decision 2026-08-29): the 1:1 view refetches every 3s and the
 * thread list every 7s, only while document.visibilityState === 'visible';
 * intervals are cleared on unmount. New messages are merged by id (no clobbering
 * of state while a message is being sent or composed). Polling is skipped when
 * there is no condo context or no selected conversation.
 */

interface Attachment {
  type: 'image' | 'audio' | 'contact' | 'file';
  name: string;
  mimeType: string;
  size: number;
  data: string; // data URI (data:<mime>;base64,...) or plain text for vCard
}

// Parse the messages-table JSONB `attachments` column (array or JSON string).
const parseAttachments = (raw: any): Attachment[] => {
  let arr: any = raw;
  if (typeof raw === 'string') {
    try { arr = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((a: any) => a && typeof a === 'object' && typeof a.data === 'string' && a.data)
    .map((a: any) => ({
      type: (['image', 'audio', 'contact', 'file'].includes(a.type) ? a.type : 'file') as Attachment['type'],
      name: String(a.name || 'archivo'),
      mimeType: String(a.mimeType || ''),
      size: Number(a.size) || a.data.length,
      data: a.data,
    }));
};

// Common unicode emoji set — same groups as the WallPage composer.
const EMOJI_GROUPS: string[][] = [
  '😀 😄 😁 😆 😅 😂 🙂 😉 😊 😍 😘 😜 🤔 🤗 😴 😢 😭 😤 😡 😱'.split(' '),
  '👍 👎 👏 🙏 💪 🤝 ✌️ 👋 ❤️ 💔 ✨ 🎉 🎊 🔥 💯 ⚠️ ❓ ✅ ❌ ⭐'.split(' '),
  '🏠 🏢 🌳 🚗 🐕 🐈 🌮 ☕ 🍕 🎂 🎁 📦 🔧 🛠️ 💧 💡 📱 💳 🧹 🌧️'.split(' '),
];

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// MSG-TICKS (2026-09-22, Jaime): WhatsApp-style delivery ticks, rendered ONLY
// on the sender's own messages next to the timestamp.
//   1 check  = sent (muted — inherits the bubble's timestamp tone)
//   2 checks = delivered (muted)
//   2 checks = read (accent — the bubble background IS --v-accent/--v-teal-400,
//              so the read state uses the same accent family's deep shade
//              --v-teal-800 for contrast in light and dark themes).
// Defensive fallback (msg_read_receipts_be contract): unknown/missing status
// degrades to 'sent' (1 check) instead of breaking.
// MSG-EDIT-DELETE (2026-09-22): how long a message stays deletable. The backend
// enforces the 24h window (403 after); the menu only offers "Eliminar para
// todos" inside it so users never hit a guaranteed failure. Edit is author-only,
// any time.
const EDIT_DELETE_WINDOW_MS = 24 * 60 * 60 * 1000;

function MessageTicks({ status, own, t: tMsg }: { status?: string; own: boolean; t: TFunction }) {
  if (!own) return null;
  const s = status === 'read' || status === 'delivered' ? status : 'sent';
  if (s === 'sent') {
    return <Check className="w-3.5 h-3.5 inline-block" aria-label={tMsg('messages.ticks.sent')} />;
  }
  return (
    <CheckCheck
      className={`w-3.5 h-3.5 inline-block ${s === 'read' ? 'text-[var(--v-teal-800)]' : ''}`}
      aria-label={s === 'read' ? tMsg('messages.ticks.read') : tMsg('messages.ticks.delivered')}
    />
  );
}

interface Conversation {
  id: string; // "<condoId>:<participantUserId>"
  condo_id?: string;
  participant_id?: string;
  participant_name?: string;
  condo_name?: string;
  last_message?: string;
  last_message_at?: string;
  last_message_mine?: boolean;
  unread_count?: number;
  is_group?: boolean;
  name?: string;
  participant_count?: number;
  created_by?: string;
}

interface DmMessage {
  id: string;
  condo_id?: string;
  sender_id: string;
  recipient_id?: string;
  sender_name?: string;
  content: string | null;
  attachments?: any;
  created_at: string;
  // MSG-TICKS (2026-09-22): optional delivery status from the backend
  // (msg_read_receipts_be, in flight). Only present on the requester's own
  // messages; when missing (undeployed backend / older rows) the UI falls
  // back to a single gray check (sent) and never breaks.
  status?: 'sent' | 'delivered' | 'read';
  // MSG-EDIT-DELETE (2026-09-22): edit/delete contract (msg_edit_delete_be, in
  // flight). edited_at set -> show "editado" next to the time; deleted messages
  // arrive as tombstones (deleted: true) and render muted italic for everyone.
  edited_at?: string | null;
  deleted?: boolean;
}

// MSG-INFO (2026-09-22, Jaime): per-recipient delivery/read receipts, present
// ONLY on the user's OWN messages (backend msg_read_receipts_be). Nulls mean
// "not yet". Absent/empty → the info affordance is hidden entirely.
interface StatusDetail {
  user_id: string;
  name: string;
  avatar_url?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
}

interface DirectoryMember {
  id: string;
  name: string;
  role?: string;
  unit_number?: string | null;
  condo_id?: string;
  condo_name?: string;
  unit_id?: string | null;
}

const CHAT_POLL_MS = 3000;   // 1:1 view refresh (user decision 2026-08-29)
// MSG-INFO modal: who has seen the message and at what time (WhatsApp-style).
// Sections: Visto (read), Entregado (delivered, not read), Pendiente (nothing yet).
function MessageInfoModal({ msg, onClose, t: tMsg }: { msg: DmMessage; onClose: () => void; t: TFunction }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const details: StatusDetail[] = Array.isArray((msg as any).status_details)
    ? (msg as any).status_details.filter((d: any) => d && typeof d === 'object' && d.user_id)
    : [];
  const read = details
    .filter(d => d.read_at)
    .sort((a, b) => new Date(a.read_at!).getTime() - new Date(b.read_at!).getTime());
  const delivered = details.filter(d => d.delivered_at && !d.read_at);
  const pending = details.filter(d => !d.delivered_at);

  const Row = ({ d, ts, label }: { d: StatusDetail; ts?: string | null; label?: string }) => (
    <li className="flex items-center gap-3 py-1.5">
      <Avatar name={d.name} avatarUrl={d.avatar_url} size={32} />
      <span className="flex-1 min-w-0 text-sm text-gray-900 dark:text-white truncate">{d.name || d.user_id}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{label || (ts ? fmtInfoTime(ts) : '')}</span>
    </li>
  );
  const fmtInfoTime = (ts?: string | null) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const today = new Date();
      const t = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
      return d.toDateString() === today.toDateString() ? t : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) + ' ' + t;
    } catch { return ''; }
  };

  const atts = parseAttachments(msg.attachments);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={tMsg('messages.info.dialogAria')}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 rounded-t-2xl">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">{tMsg('messages.info.title')}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label={tMsg('messages.info.close')}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
          {msg.content && <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{msg.content}</p>}
          {atts.length > 0 && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">📎 {tMsg('messages.info.attachments', { count: atts.length })}</p>}
          <p className="text-xs text-gray-400 mt-1">{tMsg('messages.info.sentAt', { time: fmtInfoTime(msg.created_at) })}</p>
        </div>
        <div className="px-4 py-2">
          {details.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-3">{tMsg('messages.info.noDetails')}</p>
          ) : (
            <>
              {read.length > 0 && (
                <div className="py-1">
                  <p className="text-xs font-semibold text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)] uppercase tracking-wide mb-1">{tMsg('messages.info.section.read')}</p>
                  <ul>{read.map(d => <Row key={d.user_id} d={d} ts={d.read_at} />)}</ul>
                </div>
              )}
              {delivered.length > 0 && (
                <div className="py-1">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{tMsg('messages.info.section.delivered')}</p>
                  <ul>{delivered.map(d => <Row key={d.user_id} d={d} ts={d.delivered_at} />)}</ul>
                </div>
              )}
              {pending.length > 0 && (
                <div className="py-1">
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">{tMsg('messages.info.section.pending')}</p>
                  <ul>{pending.map(d => <Row key={d.user_id} d={d} label={tMsg('messages.info.section.pending')} />)}</ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const LIST_POLL_MS = 7000;   // thread list refresh (lighter cadence)

function toast(msg: string, type: 'success' | 'error' = 'success') {
  const el = document.createElement('div');
  el.className = `fixed top-4 right-4 z-[60] ${type === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white px-4 py-2 rounded-lg shadow-lg`;
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

const ROLE_PRECEDENCE: Record<string, number> = { propietario: 3, admin: 2, super_admin: 2, resident: 1, empleado: 1 };

/**
 * One entry per user (2026-08-31): /me/dm-directory returns a row per
 * membership role (e.g. Jaime as propietario AND resident of Unidad 001).
 * Keep the row with the highest-precedence role so the picker shows each
 * person once with their most relevant role badge.
 */
function dedupeDirectory(items: DirectoryMember[]): DirectoryMember[] {
  const byId = new Map<string, DirectoryMember>();
  for (const item of items) {
    const prev = byId.get(item.id);
    if (!prev) { byId.set(item.id, item); continue; }
    const prevRank = ROLE_PRECEDENCE[(prev.role || '').toLowerCase()] ?? 0;
    const nextRank = ROLE_PRECEDENCE[(item.role || '').toLowerCase()] ?? 0;
    if (nextRank > prevRank || (nextRank === prevRank && !prev.condo_id && item.condo_id)) {
      byId.set(item.id, item);
    }
  }
  return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export default function MensajesPage() {
  const navigate = useNavigate();
  const goBack = useSmartBack('/');
  const { user, isAuthenticated, activeCondoId } = useAuth();
  // I18N-2B: namespaced translators — 'messages' copy + shared 'common'
  // Cancel/Save/Delete/User strings. MessageTicks/MessageInfoModal receive
  // tMsg as a prop (module scope has no hook context).
  const { t: tRaw } = useTranslation('messages');
  const tMsg = tRaw as TFunction;
  const { t: tCommonRaw } = useTranslation('common');
  const tCommon = tCommonRaw as TFunction;
  // Prefer the auth context's resolved active condo; fall back to user.condoId (same as WallPage).
  const condoId = (isAuthenticated && activeCondoId) || user?.condoId;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [convLoading, setConvLoading] = useState(true);
  const [convError, setConvError] = useState<string | null>(null);
  // E3/OBS-22 (2026-10-25): true when the conversation list failed because the
  // account has no condo membership at all. Renders the friendly join/create
  // condo onboarding instead of the generic error banner. The backend signals
  // this case with 403 { code: 'MEMBERSHIP_REQUIRED' } on GET /messages/conversations
  // (routes.js: getActiveDmContextIds returns [] when there is no membership).
  const [noMembership, setNoMembership] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);

  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatMode, setNewChatMode] = useState<'direct' | 'group'>('direct');
  const [groupName, setGroupName] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [groupCondoId, setGroupCondoId] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupDetail, setGroupDetail] = useState<any>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [directory, setDirectory] = useState<DirectoryMember[]>([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirSearch, setDirSearch] = useState('');

  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // MSG-EDIT-DELETE (2026-09-22): inline edit + confirm-delete + hover menu.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [menuMsgId, setMenuMsgId] = useState<string | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Composer state (same as WallPage composer)
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);

  // MSG-INFO (2026-09-22): message-info viewer ("¿Quién vio el mensaje?").
  const [infoMsg, setInfoMsg] = useState<DmMessage | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  // In-app image viewer (lightbox) — replaces target=_blank data: URI navigation
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const sendingRef = useRef(false);

  // Silent mode (background polling / post-send refresh) must not toggle the
  // visible spinner or clear existing data: keep the list stable, only swap in
  // fresher data. Spinner stays for initial load and explicit retries only.
  const fetchConversations = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) {
      setConvLoading(true);
      setConvError(null);
    }
    try {
      const res = await api.get('/messages/conversations');
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      setConversations(items);
      setNoMembership(false); // E3/OBS-22: a successful load clears the join-condo gate
    } catch (err: any) {
      if (!silent) {
        // E3/OBS-22: 403 + code MEMBERSHIP_REQUIRED = condo-less account ->
        // friendly onboarding empty state. Any other failure keeps the real
        // error banner (network/404/other server errors).
        if (err?.response?.data?.code === 'MEMBERSHIP_REQUIRED') {
          setNoMembership(true);
          setConvError(null);
        } else {
          setNoMembership(false);
          setConvError(err?.response?.status === 404
            ? tMsg('messages.error.listNotFound')
            : tMsg('messages.error.listLoad'));
        }
        setConversations([]);
      }
      // Silent failures keep the last good list; transient poll errors are ignored.
    } finally {
      if (!silent) setConvLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (convId: string) => {
    setMsgLoading(true);
    setMsgError(null);
    try {
      const res = await api.get(`/messages/conversations/${encodeURIComponent(convId)}/messages`);
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      setMessages(items);
    } catch (err: any) {
      setMsgError(err?.response?.status === 404
        ? tMsg('messages.error.threadNotFound')
        : (err?.response?.data?.error || tMsg('messages.error.threadLoad')));
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  }, []);

  // WhatsApp-style unread badge: mark the conversation read on the backend
  // (POST /messages/conversations/:id/read) and clear the badge optimistically
  // so the list reflects the change immediately (the 7s poll reconciles anyway).
  const markConversationRead = useCallback(async (convId: string) => {
    try { await api.post(`/messages/conversations/${encodeURIComponent(convId)}/read`); }
    catch { /* transient errors are ignored; the list poll restores the badge if it failed */ }
  }, []);

  const clearUnreadBadge = useCallback((convId: string) => {
    setConversations(prev => prev.map(c => c.id === convId && (c.unread_count ?? 0) > 0 ? { ...c, unread_count: 0 } : c));
  }, []);

  // Bell/deep-link read-sync (2026-09-18): entering a conversation also marks
  // that conversation's 'message' notifications read. The backend marks them
  // via the existing per-notification PATCH (PATCH /notifications/:id/read);
  // we filter client-side by type + conv id appearing in the url field
  // (contract format '/mensajes?...'). Then the badge refreshes via the
  // standard 'vecindus:notifications-updated' event.
  const markMessageNotificationsRead = useCallback(async (convId: string) => {
    try {
      const res = await api.get('/notifications/user/me');
      const payload = res?.data?.data ?? res?.data;
      const items: any[] = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []);
      const ids = items
        .filter((n) => n?.type === 'message' && !n?.read
          && typeof n?.url === 'string'
          && decodeURIComponent(n.url).includes(convId))
        .map((n) => String(n.id));
      if (!ids.length) return;
      await Promise.all(ids.map((id) =>
        api.patch(`/notifications/${encodeURIComponent(id)}/read`).catch(() => null)
      ));
      window.dispatchEvent(new Event('vecindus:notifications-updated'));
    } catch { /* transient: the badge poller reconciles on its next tick */ }
  }, []);

  const openConversation = useCallback((convId: string) => {
    setSelectedId(convId);
    clearUnreadBadge(convId);
    markConversationRead(convId);
    markMessageNotificationsRead(convId);
  }, [clearUnreadBadge, markConversationRead, markMessageNotificationsRead]);

  const selectedConv = conversations.find(c => c.id === selectedId);
  const filteredDirectory = directory.filter(m => !dirSearch.trim() || (m.name || '').toLowerCase().includes(dirSearch.trim().toLowerCase()));

  // Deep link (2026-09-18): push/bell notifications land on /mensajes?conv=<id>
  // (backend push payload url contract). Open that conversation once on mount.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get('conv') || params.get('conversation');
    if (!target) return;
    deepLinkAppliedRef.current = true;
    openConversation(target);
  }, [openConversation]);

  // Initial load
  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { if (selectedId) fetchMessages(selectedId); }, [selectedId, fetchMessages]);
  useEffect(() => {
    if (!selectedId || !selectedConv?.is_group) { setGroupDetail(null); return; }
    api.get(`/messages/conversations/${encodeURIComponent(selectedId)}`).then(r => setGroupDetail(r.data?.data ?? r.data)).catch(() => setGroupDetail(null));
  }, [selectedId, selectedConv?.is_group]);

  // Polling: thread list (7s) — only while the tab is visible. Cleared on
  // unmount / when visibility flips. Not gated on the active condo (threads
  // are cross-condo since 2026-08-29).
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval === null) interval = setInterval(() => {
        // Skip while a send is in flight to avoid clobbering state.
        if (!sendingRef.current) fetchConversations({ silent: true });
      }, LIST_POLL_MS);
    };
    const stop = () => { if (interval !== null) { clearInterval(interval); interval = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start(); else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [fetchConversations]);

  // Polling: 1:1 view (3s) — merges by id, never while sending, only when
  // a conversation is selected and the tab is visible. Cleared on unmount.
  useEffect(() => {
    if (!selectedId) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval === null) interval = setInterval(async () => {
        if (sendingRef.current) return; // don't clobber an in-flight send
        try {
          const res = await api.get(`/messages/conversations/${encodeURIComponent(selectedId)}/messages`);
          const data = res.data?.data ?? res.data;
          const items: DmMessage[] = Array.isArray(data) ? data : (data?.items ?? []);
          setMessages(prev => {
            if (items.length === prev.length && items.every((m, i) => m.id === prev[i]?.id)) return prev; // no-op
            const byId = new Map(prev.map(m => [m.id, m]));
            items.forEach(m => byId.set(m.id, m));
            // Defensive dedupe: if a temp optimistic bubble (local-*) is still
            // present and a server message from the same sender with the same
            // content arrived within a few seconds, drop the temp bubble so the
            // sender never sees their own message twice.
            for (const m of items) {
              if (!m.sender_id || m.sender_id !== user?.id) continue;
              const t = new Date(m.created_at).getTime();
              prev.some(tmp => {
                if (!tmp.id.startsWith('local-')) return false;
                if (tmp.sender_id !== m.sender_id) return false;
                if ((tmp.content ?? null) !== (m.content ?? null)) return false;
                const dt = Math.abs(t - new Date(tmp.created_at).getTime());
                if (dt <= 10000) { byId.delete(tmp.id); return true; }
                return false;
              });
            }
            return Array.from(byId.values()).sort((a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          });
          // New messages arrived while the chat is open → they are read now.
          const changed = items.length !== messages.length || !items.every((m, i) => m.id === messages[i]?.id);
          if (changed && items.some(m => m.sender_id && m.sender_id !== user?.id)) {
            clearUnreadBadge(selectedId);
            markConversationRead(selectedId);
          }
        } catch { /* transient poll errors are silently ignored */ }
      }, CHAT_POLL_MS);
    };
    const stop = () => { if (interval !== null) { clearInterval(interval); interval = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start(); else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [selectedId, messages.length, user?.id, clearUnreadBadge, markConversationRead]);

  // Auto-scroll: programmatic container autoscroll to the bottom on load,
  // after each send, and on each poll merge (not focus-scroll).
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length, msgLoading]);

  // ---------- Member directory ("Nuevo chat") ----------
  const openNewChat = async () => {
    setShowNewChat(true);
    setNewChatMode('direct');
    setGroupName('');
    setSelectedParticipants([]);
    setGroupCondoId(condoId || '');
    setDirSearch('');
    setDirLoading(true);
    try {
      // Cross-condo directory (2026-08-29): auth-only endpoint, no active-condo
      // dependency. Returns the union of members across all shared condos.
      const res = await api.get('/me/dm-directory');
      const data = res.data?.data ?? res.data;
      // Backend returns one row per membership role (user_condo_roles), so the
      // same user can appear multiple times. Dedupe by user id, keeping the
      // highest-precedence role label: propietario > admin > resident/empleado.
      setDirectory(dedupeDirectory(Array.isArray(data?.items) ? data.items : []));
    } catch {
      toast(tMsg('messages.directory.loadError'), 'error');
      setDirectory([]);
    } finally {
      setDirLoading(false);
    }
  };

  const directoryCondos = Array.from(new Map(directory.filter(m => m.condo_id).map(m => [m.condo_id!, m.condo_name || m.condo_id!])).entries());
  const groupMembers = filteredDirectory.filter(m => !groupCondoId || m.condo_id === groupCondoId);
  const toggleParticipant = (id: string) => setSelectedParticipants(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const friendlyGroupError = (err: any) => {
    const code = err?.response?.data?.code;
    // I18N-2B: backend error codes map to namespaced copy; unknown codes fall
    // back to the backend message, then to the generic create-failure toast.
    const known: Record<string, string> = {
      STAFF_SCOPE: 'messages.group.error.STAFF_SCOPE',
      NOT_ALL_MEMBERS: 'messages.group.error.NOT_ALL_MEMBERS',
      TOO_FEW_PARTICIPANTS: 'messages.group.error.TOO_FEW_PARTICIPANTS',
      CONDO_REQUIRED: 'messages.group.error.CONDO_REQUIRED',
      INVALID_NAME: 'messages.group.error.INVALID_NAME',
    };
    return (code && known[code] && tMsg(known[code])) || err?.response?.data?.error || tMsg('messages.group.error.createFallback');
  };
  const createGroup = async () => {
    if (!groupName.trim() || selectedParticipants.length < 2 || creatingGroup) return;
    setCreatingGroup(true);
    try {
      const res = await api.post('/messages/conversations/group', { name: groupName.trim(), participant_ids: selectedParticipants, ...(groupCondoId ? { condo_id: groupCondoId } : {}) });
      const group = res.data?.data;
      if (!group?.id) throw new Error('Respuesta inválida');
      const row: Conversation = { ...group, participant_name: group.name, is_group: true, participant_count: group.participants_detail?.length || selectedParticipants.length + 1 };
      setConversations(prev => [row, ...prev.filter(c => c.id !== row.id)]);
      setShowNewChat(false); setSelectedId(row.id); setGroupDetail(group); setNewChatMode('direct');
      toast(tMsg('messages.group.created'));
    } catch (err: any) { toast(friendlyGroupError(err), 'error'); }
    finally { setCreatingGroup(false); }
  };

  const startChatWith = (member: DirectoryMember) => {
    // Any shared condo works (backend accepts it); use the directory entry's
    // condo_id so the conversation id stays <condo_id>:<userId>.
    // Fix 2026-09-22 (standalone homes): standalone co-members (owner <-> the
    // unit's residents) share no condo — the directory returns the SHARED
    // unit_id (condo_id NULL), and DMs live in that unit-scoped context.
    const sharedCondoId = member.condo_id || member.unit_id || condoId;
    if (!sharedCondoId) { toast(tMsg('messages.error.noSharedCondo'), 'error'); return; }
    const convId = `${sharedCondoId}:${member.id}`;
    setConversations(prev => prev.some(c => c.id === convId) ? prev : [{
      id: convId, condo_id: sharedCondoId, participant_id: member.id,
      participant_name: member.name, condo_name: member.condo_name,
      last_message: '', last_message_at: undefined,
    }, ...prev]);
    setShowNewChat(false);
    setSelectedId(convId);
  };

  // ---------- MSG-EDIT-DELETE (2026-09-22): edit + delete own messages ----------
  // PATCH /api/communication/messages/:id {content} -> author-only, any time,
  // response includes edited_at. DELETE .../:id -> author-only, 24h window
  // (backend 403s later). If the backend is not deployed yet a request 404s:
  // the optimistic state is kept consistent and the user gets a clear toast.
  const startEditing = (msg: DmMessage) => {
    setMenuMsgId(null);
    setDeleteConfirmId(null);
    setEditingId(msg.id);
    setEditValue(msg.content || '');
    setEditError('');
    requestAnimationFrame(() => {
      const el = editTextareaRef.current;
      if (el) {
        el.focus();
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValue('');
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editingId || editSaving) return;
    const content = editValue.trim();
    if (!content) { setEditError(tMsg('messages.edit.empty')); return; }
    setEditSaving(true);
    setEditError('');
    try {
      const res = await api.patch(`/communication/messages/${encodeURIComponent(editingId)}`, { content });
      const data = res.data?.data;
      const editedAt: string | undefined = data?.edited_at || data?.message?.edited_at || new Date().toISOString();
      // Author-side optimistic update; the 3s poll merge picks up the server
      // copy anyway (merged by id, no clobbering).
      setMessages(prev => prev.map(m => m.id === editingId ? { ...m, content, edited_at: editedAt } : m));
      setEditingId(null);
      setEditValue('');
      toast(tMsg('messages.edit.done'));
    } catch (err: any) {
      setEditError(err?.response?.data?.error || tMsg('messages.edit.failed'));
    } finally {
      setEditSaving(false);
    }
  };

  const requestDelete = (msgId: string) => {
    setMenuMsgId(null);
    setEditingId(null);
    setDeleteConfirmId(msgId);
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/communication/messages/${encodeURIComponent(deleteConfirmId)}`);
      // Tombstone in place (same shape the poll will deliver): everyone sees
      // "Este mensaje fue eliminado"; content/attachments are dropped.
      setMessages(prev => prev.map(m => m.id === deleteConfirmId ? { ...m, deleted: true, content: null, attachments: undefined } : m));
      setDeleteConfirmId(null);
      toast(tMsg('messages.delete.done'));
      fetchConversations({ silent: true });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // msg_edit_delete_be not live yet: nothing was deleted server-side.
        setDeleteConfirmId(null);
        toast(tMsg('messages.delete.notAvailable'), 'error');
      } else if (err?.response?.status === 403) {
        setDeleteConfirmId(null);
        toast(tMsg('messages.delete.windowExpired'), 'error');
      } else {
        toast(err?.response?.data?.error || tMsg('messages.delete.failed'), 'error');
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- Send (DM endpoint: text optional when attachments present) ----------
  const handleSend = async () => {
    if (!selectedId || sending) return;
    if (!input.trim() && pendingAttachments.length === 0) return;
    const [convCondoId, recipientId] = selectedId.split(':');
    const isGroup = Boolean(selectedConv?.is_group);
    if (!convCondoId || !recipientId) { setSendError(tMsg('messages.send.invalidConversation')); return; }

    setSending(true);
    sendingRef.current = true;
    setSendError('');
    const optimistic: DmMessage = {
      id: `local-${Date.now()}`,
      condo_id: convCondoId,
      sender_id: user?.id || '',
      recipient_id: recipientId,
      sender_name: user?.name || '',
      content: input.trim() || null,
      attachments: pendingAttachments.length ? pendingAttachments : undefined,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    const sentContent = input.trim();
    const sentAttachments = pendingAttachments;
    setInput('');
    setPendingAttachments([]);
    setShowEmoji(false);
    resetTextareaHeight();
    try {
      // F110 spec 2026-08-29 (cross-condo update): POST /api/communication/condo/:condoId/dm
      // using the conversation's shared condo id — the active context is irrelevant.
      const res = isGroup
        ? await api.post(`/messages/conversations/${encodeURIComponent(selectedId)}/messages`, { content: sentContent, attachments: sentAttachments })
        : await api.post(`/communication/condo/${convCondoId}/dm`, { recipient_id: recipientId, content: sentContent, attachments: sentAttachments });
      // Response shapes differ by endpoint (bug 2026-08-31, group send doubled):
      // - DM  /communication/condo/:condoId/dm  → data: { message }
      // - Group /messages/conversations/:id/messages → data: { ...message, is_group }
      // If data.message is missing but data itself looks like a message (has an id),
      // use it — otherwise the optimistic temp-id bubble is never replaced and the
      // 3s poll merges the server copy as a second bubble for the sender.
      const data = res.data?.data;
      const saved = data?.message ?? (data && typeof data === 'object' && data.id ? data : undefined);
      setMessages(prev => prev.map(m => m.id === optimistic.id ? {
        ...m,
        id: saved?.id || m.id,
        content: saved?.content ?? m.content,
        attachments: sentAttachments.length ? sentAttachments : parseAttachments(saved?.attachments),
        created_at: saved?.created_at || m.created_at,
      } : m));
      // Refresh thread list previews in the background (no spinner flash)
      fetchConversations({ silent: true });
    } catch (err: any) {
      // Roll back the optimistic bubble and restore the draft
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInput(sentContent);
      setPendingAttachments(sentAttachments);
      requestAnimationFrame(() => autoResizeTextarea());
      setSendError(err?.response?.data?.error === 'FILE_TOO_LARGE'
        ? tMsg('messages.send.fileTooLarge')
        : (err?.response?.data?.error || tMsg('messages.send.failed')));
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  // WhatsApp-style auto-expand: start at 1 line, grow up to ~5 lines (max 120px),
  // scrollable after that. The pill grows with the textarea.
  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  // Reset the auto-expanded textarea back to one line (after send).
  const resetTextareaHeight = () => {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) el.style.height = 'auto';
    });
  };

  // Jaime amendment 2026-08-29 (screenshots 03:48): the textarea rests at ONE
  // line; it expands upward only while typing and collapses back to one line
  // on blur (even if multi-line text remains — overflow-y scrolls it).
  const collapseTextareaOnBlur = () => {
    const el = textareaRef.current;
    if (el) el.style.height = '44px';
  };

  // ---------- Composer helpers (same as WallPage) ----------
  const insertAtCursor = useCallback((text: string) => {
    const ta = textareaRef.current;
    if (!ta) { setInput(prev => prev + text); return; }
    const start = ta.selectionStart ?? input.length;
    const end = ta.selectionEnd ?? start;
    const next = input.slice(0, start) + text + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  }, [input]);

  // UX fix 2026-09-22 (Jaime): photos are compressed client-side (canvas
  // downscale + JPEG re-encode, marker UXFIX-IMGCOMPRESS-20260922) so typical
  // 2-6 MB phone photos fit the 1.5 MB server cap. Non-image attachments
  // (kept for future file types) still enforce the cap with a clear message.
  const addImageFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).slice(0, 4).forEach(file => {
      if (!file.type.startsWith('image/')) {
        if (file.size > MAX_ATTACHMENT_BASE64 * 0.75) {
          setSendError(tMsg('messages.file.tooLarge', { name: file.name }));
        }
        return;
      }
      compressImageUnderCap(file)
        .then(data => {
          setPendingAttachments(prev => [...prev, {
            type: 'image', name: file.name, mimeType: 'image/jpeg', size: data.length, data,
          }]);
        })
        .catch(() => {
          setSendError(tMsg('messages.image.processFailed', { name: file.name }));
        });
    });
  };

  const startRecording = async () => {
    setRecordError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setRecordError(tMsg('messages.voice.unsupported'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => {
          const data = String(reader.result || '');
          if (data.length > MAX_ATTACHMENT_BASE64) {
            setRecordError(tMsg('messages.voice.tooLong'));
            return;
          }
          setPendingAttachments(prev => [...prev, {
            type: 'audio',
            name: `nota-de-voz-${new Date().toISOString().slice(11, 19)}.webm`,
            mimeType: blob.type || 'audio/webm',
            size: data.length,
            data,
          }]);
        };
        reader.readAsDataURL(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setRecordError(tMsg('messages.voice.micError'));
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const shareMyContact = () => {
    const name = user?.name || tMsg('messages.neighborFallback');
    const anyUser: any = user || {};
    const email = anyUser.email || '';
    const phone = anyUser.phone || anyUser.phoneNumber || anyUser.telefono || '';
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${name}`,
      `N:${name.split(' ').slice(-1)[0] || name};${name.split(' ')[0] || ''};;;`,
      email ? `EMAIL:${email}` : '',
      phone ? `TEL;TYPE=CELL:${phone}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');
    const data = `data:text/vcard;base64,${btoa(unescape(encodeURIComponent(vcard)))}`;
    setPendingAttachments(prev => prev.some(a => a.type === 'contact')
      ? prev
      : [...prev, { type: 'contact', name: `${name}.vcf`, mimeType: 'text/vcard', size: data.length, data }]);
  };

  const removePending = (idx: number) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const canRenameGroup = Boolean(selectedConv?.is_group && (String(groupDetail?.created_by) === String(user?.id) || ['admin', 'super_admin'].includes(String((user as any)?.role || ''))));
  const renameGroup = async () => {
    if (!selectedId || !renameValue.trim() || renaming) return;
    const previous = selectedConv?.name || selectedConv?.participant_name || '';
    setRenaming(true); setConversations(prev => prev.map(c => c.id === selectedId ? { ...c, name: renameValue.trim(), participant_name: renameValue.trim() } : c));
    try { const r = await api.patch(`/messages/conversations/${encodeURIComponent(selectedId)}`, { name: renameValue.trim() }); setGroupDetail((d: any) => ({ ...d, ...(r.data?.data || {}) })); setRenameOpen(false); toast(tMsg('messages.group.renamed')); fetchConversations({ silent: true }); }
    catch (err: any) { setConversations(prev => prev.map(c => c.id === selectedId ? { ...c, name: previous, participant_name: previous } : c)); toast(err?.response?.data?.code === 'FORBIDDEN' ? tMsg('messages.group.renameForbidden') : tMsg('messages.group.renameFailed'), 'error'); }
    finally { setRenaming(false); }
  };

  // ---------- Rendering helpers (same attachment renderer as WallPage) ----------
  const renderAttachment = (a: Attachment, i: number, own: boolean) => {
    if (a.type === 'image') {
      return (
        <button
          key={i}
          type="button"
          onClick={() => setLightboxImage({ src: a.data, alt: a.name })}
          className="block cursor-zoom-in"
          aria-label={tMsg('messages.attachment.viewImage', { name: a.name })}
        >
          <img
            src={a.data}
            alt={a.name}
            className="max-h-56 max-w-full rounded-lg border border-black/10 object-cover hover:opacity-90 transition-opacity"
            loading="lazy"
          />
        </button>
      );
    }
    if (a.type === 'audio') {
      return <audio key={i} controls src={a.data} className="w-full max-w-[240px] min-w-0 h-10" />;
    }
    if (a.type === 'contact') {
      return (
        <a
          key={i}
          href={a.data}
          download={a.name}
          className="flex items-center gap-3 bg-white/90 border border-[var(--v-border-light)] rounded-lg p-3 w-full max-w-[240px] min-w-0 hover:border-[var(--v-teal-400)] transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-[var(--v-teal-400)]/10 flex items-center justify-center flex-shrink-0">
            <ContactIcon className="w-5 h-5 text-[var(--v-teal-400)]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--v-text-primary)] truncate">{a.name.replace(/\.vcf$/i, '')}</p>
            <p className="text-xs text-[var(--v-text-tertiary)]">{tMsg('messages.attachment.contactCard')}</p>
          </div>
        </a>
      );
    }
    return (
      <a
        key={i}
        href={a.data}
        download={a.name}
        className={`flex items-center gap-2 text-sm underline ${own ? 'text-white' : 'text-[var(--v-teal-700)]'}`}
      >
        📎 {a.name} ({formatBytes(a.size)})
      </a>
    );
  };

  const formatTime = (ts?: string) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const formatDate = (ts?: string) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return formatTime(ts);
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    } catch { return ''; }
  };

  return (
    <div className="flex flex-col h-[100dvh] min-h-0 overflow-hidden overflow-x-hidden bg-[var(--v-base-bg)] dark:bg-[var(--v-surface-1)]" style={{ fontFamily: 'var(--v-font-sans)' }}>
      <header className="flex-shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-gray-100 dark:border-[var(--v-surface-3)] z-40">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-3">
            <button onClick={goBack} className="p-2 text-gray-600 dark:text-gray-300 hover:text-[var(--v-teal-700)] hover:bg-[var(--v-teal-400)]/10 dark:hover:bg-[var(--v-surface-1)] rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[var(--v-teal-500)] to-[var(--v-teal-700)] rounded-xl flex items-center justify-center shadow-lg">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-[var(--v-text-primary)]">{tMsg('messages.title')}</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tMsg('messages.subtitle')}</p>
              </div>
            </div>
          </div>
          <button
            onClick={openNewChat}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--v-teal-400)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--v-teal-500)] transition-colors"
            title={tMsg('messages.newChatTitle')}
          >
            <Plus className="w-4 h-4" /> {tMsg('messages.newChat')}
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {noMembership ? (
          /* E3/OBS-22: condo-less account — reuse the app's existing condo
             onboarding (same component LensDashboard uses for this state). */
          <div className="w-full overflow-auto bg-[var(--v-base-bg)]">
            <div className="p-4 md:p-6 min-h-full flex items-center justify-center">
              <CondoOnboarding onLinked={() => { setNoMembership(false); fetchConversations(); }} />
            </div>
          </div>
        ) : (
        <>
        {/* ── Left: Thread List (existing DMs, last-message preview) ── */}
        <div className={`min-w-0 w-full md:w-80 lg:w-96 border-r border-gray-100 dark:border-[var(--v-surface-3)] flex flex-col ${selectedId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={tMsg('messages.searchConversations')}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-[var(--v-surface-1)] text-gray-900 dark:text-white focus:ring-2 focus:ring-[var(--v-teal-400)]"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {convLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> {tMsg('messages.loading')}
              </div>
            ) : convError ? (
              <div className="p-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-700 dark:text-red-300 text-sm font-medium mb-1">{tMsg('messages.errorTitle')}</p>
                <p className="text-red-500 text-xs mb-3">{convError}</p>
                <button onClick={() => fetchConversations()} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700">{tMsg('messages.retry')}</button>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <Mail className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">{tMsg('messages.empty.threadsTitle')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{tMsg('messages.empty.threadsBody')}</p>
                <button onClick={openNewChat} className="flex items-center gap-2 px-3 py-2 bg-[var(--v-teal-400)] text-white text-sm rounded-lg hover:bg-[var(--v-teal-500)]">
                  <Plus className="w-4 h-4" /> {tMsg('messages.newChat')}
                </button>
              </div>
            ) : (
              conversations.map(conv => {
                const hasUnread = (conv.unread_count ?? 0) > 0;
                return (
                <button
                  key={conv.id}
                  onClick={() => openConversation(conv.id)}
                  className={`w-full flex items-start gap-3 p-3 text-left border-b border-gray-50 dark:border-[var(--v-surface-3)]/50 transition ${selectedId === conv.id ? 'bg-[var(--v-teal-400)]/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/60'}`}
                >
                  {/* FEATURE (2026-09-01, Jaime ruling): shared <Avatar> for 1:1 conversations; groups keep the group icon. */}
                  {conv.is_group ? (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-[var(--v-teal-600)] flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <Avatar name={conv.participant_name} avatarUrl={(conv as any).participant_avatar_url ?? null} size={40} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{conv.is_group ? (conv.name || tMsg('messages.group.defaultName')) : (conv.participant_name || tCommon('common.user'))}</p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`text-xs ${hasUnread ? 'text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)] font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>{formatDate(conv.last_message_at)}</span>
                        {(conv.unread_count ?? 0) > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--v-teal-400)] dark:bg-[var(--v-teal-500)] text-white text-[10px] font-semibold flex items-center justify-center" title={tMsg('messages.unreadTitle', { count: conv.unread_count ?? 0 })}>
                            {(conv.unread_count ?? 0) > 99 ? '99+' : conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                      {conv.last_message_mine && conv.last_message ? tMsg('messages.preview.you') : ''}{conv.last_message || tMsg('messages.preview.none')}
                    </p>
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>

        {infoMsg && <MessageInfoModal msg={infoMsg} onClose={() => setInfoMsg(null)} t={tMsg} />}

        {/* ── Right: 1:1 Chat Thread ── */}
        <div className={`flex-1 min-h-0 min-w-0 w-full flex flex-col ${selectedId ? 'flex' : 'hidden md:flex'}`}>
          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
              <MessageCircle className="w-16 h-16 text-gray-200 mb-4" />
              <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">{tMsg('messages.empty.threadTitle')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{tMsg('messages.empty.threadBody')}</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="flex-shrink-0 flex items-center gap-3 p-3 border-b border-gray-100 dark:border-[var(--v-surface-3)] bg-white dark:bg-[var(--v-surface-1)]">
                <button onClick={() => setSelectedId(null)} className="md:hidden p-1 text-gray-500 hover:text-gray-700 dark:hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                {selectedConv?.is_group ? (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-[var(--v-teal-600)] flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <Avatar name={selectedConv?.participant_name} avatarUrl={(selectedConv as any)?.participant_avatar_url ?? null} size={36} />
                )}
                <div className="flex-1 min-w-0">
                  {renameOpen ? (
                    <div className="flex items-center gap-1"><input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') renameGroup(); if (e.key === 'Escape') setRenameOpen(false); }} maxLength={120} className="w-full max-w-xs px-2 py-1 text-sm border rounded-lg dark:bg-slate-700 dark:text-white" /><button onClick={renameGroup} disabled={renaming} className="p-1 text-[var(--v-teal-500)]"><Check className="w-4 h-4" /></button></div>
                  ) : <div className="flex items-center gap-1"><p className="font-medium text-sm text-gray-900 dark:text-white truncate">{selectedConv?.is_group ? (selectedConv?.name || tMsg('messages.group.defaultName')) : (selectedConv?.participant_name || tCommon('common.user'))}</p>{canRenameGroup && <button onClick={() => { setRenameValue(selectedConv?.name || ''); setRenameOpen(true); }} title={tMsg('messages.group.rename')} className="p-1 text-gray-400 hover:text-[var(--v-teal-500)]"><Pencil className="w-3.5 h-3.5" /></button>}</div>}
                  <p className="text-xs text-gray-400">{selectedConv?.is_group ? tMsg('messages.group.participants', { count: selectedConv?.participant_count || groupDetail?.participants_detail?.length || 0 }) : (selectedConv?.condo_name || '')}</p>
                </div>
              </div>

              {/* Messages */}
              <div ref={messagesContainerRef} className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-4 space-y-3 bg-gray-50 dark:bg-[var(--v-surface-1)]">
                {msgLoading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" /> {tMsg('messages.loadingMessages')}
                  </div>
                ) : msgError ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                    <p className="text-red-700 dark:text-red-300 text-sm">{msgError}</p>
                    <button onClick={() => fetchMessages(selectedId)} className="mt-2 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700">{tMsg('messages.retry')}</button>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <MessageCircle className="w-12 h-12 text-gray-200 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">{tMsg('messages.empty.noMessages')}</p>
                    <p className="text-xs text-gray-400 mt-1">{tMsg('messages.empty.sendFirst')}</p>
                  </div>
                ) : (
                  messages.map(msg => {
                    const isOwn = msg.sender_id === user?.id;
                    const atts = parseAttachments(msg.attachments);
                    // MSG-EDIT-DELETE (2026-09-22): deleted messages arrive as
                    // tombstones {deleted:true} -> muted italic, no content, for everyone.
                    if (msg.deleted) {
                      return (
                        <div key={msg.id} className={`flex w-full ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] min-w-0 rounded-2xl px-4 py-2 border ${isOwn ? 'bg-[var(--v-teal-400)]/10 border-[var(--v-teal-400)]/30 rounded-br-sm' : 'bg-white dark:bg-[var(--v-surface-1)] border-gray-100 dark:border-[var(--v-surface-3)] rounded-bl-sm'}`}>
                            <p className="text-sm italic text-gray-400 dark:text-gray-500">{tMsg('messages.deleted.tombstone')}</p>
                            <p className={`text-xs mt-1 text-right flex items-center justify-end gap-1 ${isOwn ? 'text-teal-100/70' : 'text-gray-400'}`}>{formatTime(msg.created_at)}{isOwn && <MessageTicks status={msg.status} own={isOwn} t={tMsg} />}</p>
                          </div>
                        </div>
                      );
                    }
                    const isEditing = editingId === msg.id;
                    const isDeleteConfirm = deleteConfirmId === msg.id;
                    const menuOpen = menuMsgId === msg.id;
                    const canEdit = isOwn && !isEditing;
                    const canDelete = isOwn && !isEditing && (() => { try { return Date.now() - new Date(msg.created_at).getTime() < EDIT_DELETE_WINDOW_MS; } catch { return false; } })();
                    // UX 2026-09-22 (Jaime): ONE affordance on own messages —
                    // 3-dot menu (Info / Editar / Eliminar). No separate edit
                    // icon, no long-press, no inline ⓘ.
                    const hasMenu = isOwn && !isEditing;
                    return (
                      <div key={msg.id} className={`flex w-full ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className="relative max-w-[80%] min-w-0 group/msg">
                          {/* MSG-EDIT-DELETE: own-message action menu — hover on
                              desktop, always visible tap target on mobile */}
                          {hasMenu && (
                            <div className={`absolute top-0 ${isOwn ? 'right-full mr-1' : 'left-full ml-1'} z-10`}>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setMenuMsgId(menuOpen ? null : msg.id); }}
                                className="w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 shadow-sm flex items-center justify-center text-gray-400 hover:text-[var(--v-teal-500)] hover:border-[var(--v-teal-400)] transition md:opacity-0 md:group-hover/msg:opacity-100 focus:opacity-100 focus:outline-none"
                                aria-label={tMsg('messages.menu.open')}
                                aria-expanded={menuOpen}
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                              {menuOpen && (
                                <>
                                  <div className="fixed inset-0 z-20" onClick={() => setMenuMsgId(null)} aria-hidden="true" />
                                  <div
                                    className="absolute bottom-8 left-0 z-30 w-44 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-600 rounded-xl shadow-lg overflow-hidden"
                                    role="menu" aria-label={tMsg('messages.menu.open')}
                                    onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setMenuMsgId(null); } }}
                                  >
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={() => { setMenuMsgId(null); setInfoMsg(msg); }}
                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-left"
                                    >
                                      <Info className="w-4 h-4 text-gray-400" /> {tMsg('messages.menu.info')}
                                    </button>
                                    {canEdit && (
                                      <button
                                        type="button"
                                        onClick={() => startEditing(msg)}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-left"
                                      >
                                        <Pencil className="w-4 h-4 text-gray-400" /> {tMsg('messages.edit.menu')}
                                      </button>
                                    )}
                                    {canDelete && (
                                      <button
                                        type="button"
                                        onClick={() => requestDelete(msg.id)}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 text-left"
                                      >
                                        <Trash2 className="w-4 h-4" /> {tMsg('messages.delete.menu')}
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          <div
                            className={`min-w-0 overflow-hidden rounded-2xl px-4 py-2 ${isOwn ? 'bg-[var(--v-teal-400)] dark:bg-[var(--v-teal-500)] text-white rounded-br-sm' : 'bg-white dark:bg-[var(--v-surface-1)] text-gray-900 dark:text-white rounded-bl-sm border border-gray-100 dark:border-[var(--v-surface-3)]'}`}
                          >
                            {!isOwn && msg.sender_name && (
                              <p className="text-xs font-medium text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)] mb-0.5">{msg.sender_name}</p>
                            )}
                            {isEditing ? (
                              <div className="w-56 sm:w-72 max-w-full">
                                <textarea
                                  ref={editTextareaRef}
                                  value={editValue}
                                  onChange={(e) => { setEditValue(e.target.value); const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 140)}px`; }}
                                  onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); } if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); } }}
                                  maxLength={1000}
                                  autoFocus
                                  disabled={editSaving}
                                  rows={2}
                                  className="w-full text-sm bg-white/95 dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg border border-[var(--v-teal-300)] focus:ring-2 focus:ring-white/60 resize-none px-2 py-1.5"
                                />
                                {editError && <p className="text-xs text-red-100 bg-red-500/60 rounded px-2 py-1 mt-1">{editError}</p>}
                                <div className="flex items-center justify-end gap-2 mt-1.5">
                                  <button type="button" onClick={cancelEditing} disabled={editSaving} className="px-2.5 py-1 text-xs rounded-lg border border-white/50 text-white/95 hover:bg-white/10 disabled:opacity-50">{tCommon('common.cancel')}</button>
                                  <button type="button" onClick={saveEdit} disabled={editSaving || !editValue.trim()} className="px-2.5 py-1 text-xs rounded-lg bg-white text-[var(--v-teal-700)] font-semibold hover:bg-teal-50 disabled:opacity-50 flex items-center gap-1">{editSaving && <Loader2 className="w-3 h-3 animate-spin" />} {tCommon('common.save')}</button>
                                </div>
                              </div>
                            ) : isDeleteConfirm ? (
                              <div className="w-56 sm:w-64 max-w-full">
                                <p className="text-sm">{tMsg('messages.delete.confirm')}</p>
                                <div className="flex items-center justify-end gap-2 mt-1.5">
                                  <button type="button" onClick={cancelDelete} disabled={deleteBusy} className="px-2.5 py-1 text-xs rounded-lg border border-white/50 text-white/95 hover:bg-white/10 disabled:opacity-50">{tCommon('common.cancel')}</button>
                                  <button type="button" onClick={confirmDelete} disabled={deleteBusy} className="px-2.5 py-1 text-xs rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">{deleteBusy && <Loader2 className="w-3 h-3 animate-spin" />} {tCommon('common.delete')}</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {msg.content && <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>}
                                {atts.length > 0 && (
                                  <div className={`flex flex-col gap-2 ${msg.content ? 'mt-2' : ''}`}>
                                    {atts.map((a, i) => renderAttachment(a, i, isOwn))}
                                  </div>
                                )}
                                <p className={`text-xs mt-1 text-right flex items-center justify-end gap-1 ${isOwn ? 'text-teal-100' : 'text-gray-400'}`}>
                                  {formatTime(msg.created_at)}
                                  {isOwn && msg.edited_at && <span className="italic opacity-80">{tMsg('messages.status.edited')}</span>}
                                  {isOwn && <MessageTicks status={msg.status} own={isOwn} t={tMsg} />}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Composer — same as WallPage (emoji / imagen / nota de voz / contacto) */}
              <div className="flex-shrink-0 p-3 border-t border-gray-100 dark:border-[var(--v-surface-3)] bg-white dark:bg-[var(--v-surface-1)] relative">
                {pendingAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingAttachments.map((a, i) => (
                      <div key={i} className="relative group">
                        {a.type === 'image' ? (
                          <img src={a.data} alt={a.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center p-1 text-center">
                            <span className="text-lg leading-none">{a.type === 'audio' ? '🎤' : '👤'}</span>
                            <span className="text-[9px] text-gray-400 truncate w-full">{a.type === 'audio' ? tMsg('messages.voice.title') : a.name}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePending(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                          aria-label={tMsg('messages.composer.removeAttachment')}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {showEmoji && (
                  <div className="absolute bottom-full left-3 mb-2 z-20 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-600 rounded-xl shadow-lg p-3 w-72 max-w-[calc(100vw-2rem)]">
                    {EMOJI_GROUPS.map((group, gi) => (
                      <div key={gi} className={gi > 0 ? 'mt-2' : ''}>
                        <div className="grid grid-cols-10 gap-1">
                          {group.map((emoji, ei) => (
                            <button
                              key={ei}
                              type="button"
                              onClick={() => insertAtCursor(emoji)}
                              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-lg"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Jaime amendment 2026-08-29: input on TOP (auto-expands upward — composer is
                    bottom-pinned so growth pushes the top edge up), button row BELOW:
                    emoji / imagen / voz / contacto left, counter + send right. Same design as Muro. */}
                <div className="flex flex-col gap-2 min-w-0">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onInput={autoResizeTextarea}
                    onBlur={collapseTextareaOnBlur}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder={tMsg('messages.composer.placeholder')}
                    rows={1}
                    maxLength={1000}
                    disabled={sending}
                    className="w-full px-3 py-2.5 text-base border border-gray-200 dark:border-slate-600 rounded-2xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[var(--v-teal-400)] resize-none text-left overflow-y-auto"
                    style={{ width: '100%', textAlign: 'left', minHeight: '44px', maxHeight: '120px', lineHeight: '1.5' }}
                  />

                  <div className="flex items-center gap-1 min-w-0">
                    {/* LEFT: emoji / imagen / nota de voz / contacto */}
                    <button type="button" onClick={() => setShowEmoji(v => !v)} title={tMsg('messages.composer.emojiTitle')}
                      className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0 ${showEmoji ? 'bg-gray-100 dark:bg-slate-700' : ''}`}>
                      <Smile className="w-4 h-4 text-gray-500" />
                    </button>
                    <button type="button" onClick={() => imageInputRef.current?.click()} title={tMsg('messages.composer.imageTitle')}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0">
                      <ImageIcon className="w-4 h-4 text-gray-500" />
                    </button>
                    <button type="button" onClick={recording ? stopRecording : startRecording} title={recording ? tMsg('messages.composer.stopRecording') : tMsg('messages.voice.title')}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${recording ? 'bg-red-100 animate-pulse' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                      {recording ? <Square className="w-4 h-4 text-red-500" /> : <Mic className="w-4 h-4 text-gray-500" />}
                    </button>
                    <button type="button" onClick={shareMyContact} title={tMsg('messages.composer.contactTitle')}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0">
                      <ContactIcon className="w-4 h-4 text-gray-500" />
                    </button>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { addImageFiles(e.target.files); e.target.value = ''; }}
                    />

                    {/* RIGHT: counter + send */}
                    <span className="text-xs text-gray-400 ml-auto mr-2 flex-shrink-0">{input.length}/1000</span>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={sending || (!input.trim() && pendingAttachments.length === 0)}
                      className="p-2.5 bg-[var(--v-teal-400)] text-white rounded-full hover:bg-[var(--v-teal-500)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                    >
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {recording && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" /> {tMsg('messages.voice.recording')}
                  </p>
                )}
                {recordError && <p className="text-xs text-red-500 mt-1">{recordError}</p>}
                {sendError && (
                  <div className="mt-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-300">{sendError}</p>
                  </div>
                )}
              </div>
      </>
          )}
        </div>
          </>
        )}
      </div>

      {lightboxImage && (
        <ImageLightbox src={lightboxImage.src} alt={lightboxImage.alt} onClose={() => setLightboxImage(null)} />
      )}

      {/* ── Nuevo chat: member directory picker ── */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setShowNewChat(false)}>
          <div className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
              <h2 className="font-bold text-gray-900 dark:text-white">{newChatMode === 'group' ? tMsg('messages.newGroup') : tMsg('messages.newChat')}</h2>
              <button onClick={() => setShowNewChat(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-2 px-3 pt-3">
              <button onClick={() => setNewChatMode('direct')} className={`flex-1 py-2 text-sm rounded-lg font-medium ${newChatMode === 'direct' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300'}`}>{tMsg('messages.direct')}</button>
              <button onClick={() => setNewChatMode('group')} className={`flex-1 py-2 text-sm rounded-lg font-medium ${newChatMode === 'group' ? 'bg-[var(--v-teal-400)] text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300'}`}>{tMsg('messages.newGroup')}</button>
            </div>
            {newChatMode === 'group' && <div className="px-3 pt-3"><input value={groupName} onChange={e => setGroupName(e.target.value)} maxLength={120} placeholder={tMsg('messages.groupNamePlaceholder')} className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white" />
              {directoryCondos.length > 1 && <div className="flex gap-1.5 mt-2 overflow-x-auto">{directoryCondos.map(([id, name]) => <button key={id} onClick={() => { setGroupCondoId(id); setSelectedParticipants([]); }} className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${groupCondoId === id ? 'bg-[var(--v-teal-400)] text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300'}`}>{name}</button>)}</div>}
            </div>}
            <div className="p-3 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={dirSearch}
                  onChange={e => setDirSearch(e.target.value)}
                  placeholder={tMsg('messages.searchResidentsPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[var(--v-teal-400)]"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {dirLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> {tMsg('messages.loadingResidents')}
                </div>
              ) : filteredDirectory.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">{tMsg('messages.directory.empty')}</p>
              ) : (
                (newChatMode === 'group' ? groupMembers : filteredDirectory).map(member => (
                  <button
                    key={member.id}
                    onClick={() => newChatMode === 'group' ? toggleParticipant(member.id) : startChatWith(member)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--v-teal-400)]/10 dark:hover:bg-slate-800 transition"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-[var(--v-teal-600)] flex items-center justify-center flex-shrink-0">
                      <UserIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {member.role ? member.role.replace('_', ' ') : tMsg('messages.roleFallback')}{member.unit_number ? ` ${tMsg('messages.directory.unitSuffix', { number: member.unit_number })}` : ''}
                      </p>
                      {member.condo_name && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-[var(--v-teal-400)]/10 dark:bg-[var(--v-teal-500)]/20 text-[var(--v-teal-700)] dark:text-[var(--v-teal-300)] truncate max-w-full">
                          {member.condo_name}
                        </span>
                      )}
                    </div>
                    {newChatMode === 'group' ? <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selectedParticipants.includes(member.id) ? 'bg-[var(--v-teal-400)] border-[var(--v-teal-400)]' : 'border-gray-300'}`}>{selectedParticipants.includes(member.id) && <Check className="w-3.5 h-3.5 text-white" />}</span> : <MessageCircle className="w-4 h-4 text-[var(--v-teal-400)] flex-shrink-0" />}
                  </button>
                ))
              )}
            </div>
            {newChatMode === 'group' && <div className="p-3 border-t border-gray-100 dark:border-[var(--v-surface-3)]"><button onClick={createGroup} disabled={creatingGroup || !groupName.trim() || selectedParticipants.length < 2} className="w-full py-2.5 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold disabled:opacity-50">{creatingGroup ? tMsg('messages.group.creating') : selectedParticipants.length ? tMsg('messages.group.createWithCount', { count: selectedParticipants.length + 1 }) : tMsg('messages.group.create')}</button><p className="text-xs text-gray-400 text-center mt-1">{tMsg('messages.group.selectHint')}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}
